import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router';

import { WalletType } from '@/entities/user/model/wallet.types';
import {
  isBoosterPlaycoinPrize,
  isClickerPlaycoinPrize,
  isClickerXpPrize,
  isPartnerPrize,
  isPlaycoinPrize,
  isPremiumPrize,
} from '@/entities/wheel/helpers/predicates';
import { useWheelActions, useWheelState } from '@/entities/wheel/model/wheel.store';
import { type Prize } from '@/entities/wheel/model/wheel.types';

import { grabPrize } from '@/shared/api/wheel/grab-prize';
import { ROUTE_PATHS } from '@/shared/config/router/types';
import { openTelegramLink } from '@/shared/helpers/tg-links/open-tg-link';
import { useTelegramMainButton } from '@/shared/hooks/telegram/use-telegram-main-button';

import { CLICKER_UNIT_BY_PRIZE_TYPE } from './../ui/constants';

export interface UsePrizeMainButtonProps {
  prizes: Prize[];
  mainPrize: Prize;
  isTaken: boolean;
  hidePrizeForAd: boolean;
  setIsTaken: React.Dispatch<React.SetStateAction<boolean>>;
  setHidePrizeForAd: React.Dispatch<React.SetStateAction<boolean>>;
  setIsTabAnimating: React.Dispatch<React.SetStateAction<boolean>>;
  pathname: string;
  isShowingLootbox: boolean;
}

export const usePrizeMainButton = (params: UsePrizeMainButtonProps) => {
  const {
    mainPrize,
    prizes,
    isTaken,
    setIsTaken,
    setIsTabAnimating,
    isShowingLootbox,
    hidePrizeForAd,
    setHidePrizeForAd,
  } = params;

  const navigate = useNavigate();
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { hydrate } = useWheelActions();
  const { isActiveAd } = useWheelState();

  const [hasErrorAd, setHasErrorAd] = useState(false);
  const clickerPrize = prizes.find(
    (prize) => isClickerXpPrize(prize) || isClickerPlaycoinPrize(prize)
  );

  const resetStore = useCallback(() => {
    hydrate({
      showReward: false,
      spinning: false,
      prizes: [],
      isActiveAd,
    });
  }, [hydrate, isActiveAd]);

  const goBack = useCallback(() => {
    history.back();
    resetStore();
  }, [resetStore]);

  const handleClickMainButton = useCallback(() => {
    if (hidePrizeForAd && hasErrorAd) {
      setHidePrizeForAd(false);
      setHasErrorAd(false);
      return;
    }

    if (
      prizes.length === 1 &&
      (isPlaycoinPrize(mainPrize) || isPremiumPrize(mainPrize))
    ) {
      if (isTaken) {
        goBack();
        return;
      }

      if (isPremiumPrize(mainPrize)) {
        void grabPrize(mainPrize.id);
      }

      setIsTaken(true);
      setIsTabAnimating(true);

      return;
    }

    if (clickerPrize) {
      const unit = CLICKER_UNIT_BY_PRIZE_TYPE[clickerPrize.type];
      const duration = clickerPrize?.durationSeconds;

      const searchParams = new URLSearchParams({ unit });

      if (duration != null) {
        searchParams.append('duration', duration.toString());
      }

      const targetPath = pathname.replace(
        new RegExp(ROUTE_PATHS.PRIZE + '(/.*)?', 'gim'),
        ROUTE_PATHS.CLICKER
      );

      navigate(`${targetPath}?${searchParams.toString()}`, { replace: true });
      return;
    }

    if (mainPrize.winReturnUrl) {
      goBack();
      openTelegramLink(mainPrize.winReturnUrl);
      return;
    }

    if (isPartnerPrize(mainPrize)) {
      goBack();

      if (mainPrize.partnerUrl) {
        openTelegramLink(mainPrize.partnerUrl);
      }

      return;
    }

    if (prizes.length > 1) {
      goBack();
      return;
    }

    if (isBoosterPlaycoinPrize(mainPrize)) {
      navigate(ROUTE_PATHS.WALLET + `?wallet=${WalletType.PLAYCOIN}#EARN_MORE`, {
        replace: true,
      });
      return;
    }

    goBack();
  }, [
    hidePrizeForAd,
    hasErrorAd,
    prizes.length,
    mainPrize,
    clickerPrize,
    goBack,
    setHidePrizeForAd,
    isTaken,
    setIsTaken,
    setIsTabAnimating,
    navigate,
    pathname,
  ]);

  const makeMainButtonText = () => {
    if (!prizes.length) return '';
    if (isTaken) return t('Keep Winning');
    if (prizes.length > 1) return t('Grab Prizes');
    if (isBoosterPlaycoinPrize(mainPrize)) return t('Earn $PlayCoin');
    if (isClickerPlaycoinPrize(mainPrize)) return t('Gimme my coins');
    if (isClickerXpPrize(mainPrize)) return t('XP, Here I Come');
    return t('Grab Prize');
  };

  useTelegramMainButton(handleClickMainButton, makeMainButtonText(), {
    hidden: !prizes.length || isShowingLootbox || (hidePrizeForAd && !hasErrorAd),
  });
};
